import { useEffect, useState } from "react";
import { adapator_createDatabaseRef } from "src/adapters";
import { DB_PATHS } from "src/constants";
import { useUpdateDatabase } from "src/hooks";
import { useEsgContext } from "../../context";
import AddModal from "./AddModal";
import VersionPanel from "./VersionPanel";

const ManageVersions = ({ currentVersionId }) => {
  const [versions, setVersions] = useState([]);
  const [defaultVersion, setDefaultVersion] = useState(null);

  const [addMode, setAddMode] = useState("");

  const { orgId, respondentName, addNewVersion, currentVersionIdPath } =
    useEsgContext();

  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let unsubscribe;
    let unsubscribeGlobal;

    const fetchVersions = async () => {
      try {
        const versionsRef = adapator_createDatabaseRef([
          "global",
          "esg-survey",
          "questions",
        ]).where("org", "in", ["global", orgId]);

        unsubscribe = versionsRef.onSnapshot((snapshot) => {
          const versions = snapshot.docs.map((doc) => doc.data());
          setVersions(versions);
        });

        const globalRef = await adapator_createDatabaseRef([
          "global",
          "esg-survey",
        ]);

        unsubscribeGlobal = globalRef.onSnapshot((snapshot) => {
          if (!snapshot.exists) return;
          setDefaultVersion(snapshot.data().currentGlobalVersion);
        });
      } catch (err) {
        console.log(err);
      }
    };

    fetchVersions();

    return () => {
      unsubscribe?.();
      unsubscribeGlobal?.();
    };
  }, [refresh]);

  const createLabel = (version) => {
    const name = version.org === "global" ? "Global" : respondentName;
    const date = new Date(version.createdAt).toLocaleDateString();
    const label = `${name} - ${date}`;
    if (version.label) {
      return `${label} - (${version.label})`;
    }
    return label;
  };

  const options = versions.map((version) => ({
    label: createLabel(version),
    value: version.id,
    description: version.description,
  }));

  const optionsGlobal = versions
    .filter((v) => v.org === "global")
    .map((version) => ({
      label: createLabel(version),
      value: version.id,
    }));

  const { setOrUpdateDocument } = useUpdateDatabase();

  const handleAdd = async ({
    currentVersionId,
    orgId,
    metadata,
    currentVersionIdPath,
  }) => {
    await addNewVersion({
      currentVersionId,
      orgId,
      metadata,
      currentVersionIdPath,
    });
    setRefresh((prev) => prev + 1);
  };

  // console.log("currentVersionId :>> ", currentVersionId);
  // console.log("currentVersionIdPath :>> ", currentVersionIdPath);
  const switchVersionOrg = async (version) => {
    await setOrUpdateDocument(currentVersionIdPath, {
      currentVersion: version,
    });
  };

  const switchVersionGlobal = async (version) => {
    await setOrUpdateDocument(DB_PATHS.globalSurvey, {
      currentGlobalVersion: version,
    });
  };

  if (!currentVersionId) return null;

  return (
    <>
      <div className="rounded bg-light p-3 mb-3">
        <VersionPanel
          title="Current version used by this organisation: "
          addBtnLabel="Add org version"
          switchBtnLabel="Switch version used"
          options={options}
          currentVersionId={currentVersionId}
          onSwitch={switchVersionOrg}
          color="info"
          onAdd={() => setAddMode("org")}
        />
        <hr />
        <VersionPanel
          title="Default version: "
          addBtnLabel="Add global version"
          switchBtnLabel="Switch default version"
          options={optionsGlobal}
          currentVersionId={defaultVersion}
          onSwitch={switchVersionGlobal}
          color="success"
          onAdd={() => setAddMode("global")}
        />
      </div>
      <AddModal
        show={!!addMode}
        handleClose={() => setAddMode("")}
        onAdd={(metadata) => {
          if (addMode === "org") {
            handleAdd({
              currentVersionId,
              orgId,
              metadata,
              currentVersionIdPath,
            });
          } else {
            handleAdd({ currentVersionId, orgId: "global", metadata });
          }
        }}
        isGlobal={addMode === "global"}
      />
    </>
  );
};

export default ManageVersions;
